import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cancelOrder, getPortfolio, getQuote, placeEquityOrder } from '../api/trading'
import { placeOptionOrder } from '../api/options'
import { ApiError } from '../api/http'
import { useSetting } from './useSetting'
import { flattenSymbol, isOpenOrder, type FlattenResult } from '../lib/flatten'
import { planEquityOrder } from '../lib/equityOrderPlan'
import { getSessionPhase } from '../lib/marketHours'
import type { OrderTypeMode } from '../lib/limitPrice'
import { resolveLimitPrice, roundToCent } from '../lib/limitPrice'
import { toast } from '../toast/useToastStore'

/**
 * Flatten a symbol: cancel its open orders, confirm, sell the whole position
 * with the session-appropriate order. `isOption` rows close via the options
 * ticket path (options keep the order-type setting).
 */
export function useFlatten(args: { symbol: string; isOption?: boolean }) {
  const { symbol, isOption = false } = args
  const qc = useQueryClient()
  const [optionOrderMode] = useSetting<OrderTypeMode>('order-type-mode', 'MARKET')
  const [progress, setProgress] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async (): Promise<FlattenResult> => {
      return flattenSymbol({
        onProgress: setProgress,
        getOpenOrders: async () => {
          const pf = await getPortfolio()
          return pf.orders.filter(
            (o) => o.instrument.symbol === symbol && isOpenOrder(o),
          )
        },
        cancelOrder: (orderId) => cancelOrder(orderId),
        getPositionQty: async () => {
          const pf = await getPortfolio()
          const pos = pf.positions.find((p) => p.instrument.symbol === symbol)
          return pos ? Number(pos.quantity) : 0
        },
        placeSellAll: async (qty) => {
          if (isOption) {
            const contracts = Math.floor(qty)
            let limitPrice: number | undefined
            if (optionOrderMode === 'LIMIT') {
              const q = (await getQuote(symbol)).quotes?.[0]
              const px = resolveLimitPrice({ side: 'SELL', bid: q?.bid, ask: q?.ask, last: q?.last })
              if (px == null) throw new Error('No live price for limit order')
              limitPrice = roundToCent(px)
            }
            return placeOptionOrder({
              instrument: { symbol, type: 'OPTION' },
              side: 'SELL',
              contracts,
              openCloseIndicator: 'CLOSE',
              mode: optionOrderMode,
              limitPrice,
            })
          }
          const phase = getSessionPhase()
          const q = (await getQuote(symbol)).quotes?.[0]
          const plan = planEquityOrder({ phase, side: 'SELL', bid: q?.bid, ask: q?.ask, last: q?.last })
          if (plan == null) throw new Error('No live price for extended-hours limit order')
          // Extended hours = whole shares only; floor and leave fractional dust.
          const sellQty = plan.extendedHours ? Math.floor(qty) : qty
          if (!(sellQty > 0)) {
            throw new Error('Position is fractional-only — cannot sell in extended hours')
          }
          return placeEquityOrder({
            symbol,
            side: 'SELL',
            quantity: sellQty,
            mode: plan.mode,
            limitPrice: plan.limitPrice,
            extendedHours: plan.extendedHours,
          })
        },
      })
    },
    onSuccess: (result) => {
      setProgress(null)
      if (result.ok) {
        const parts: string[] = []
        if (result.cancelled > 0) parts.push(`${result.cancelled} order${result.cancelled === 1 ? '' : 's'} cancelled`)
        parts.push(result.soldQty > 0 ? `sold ${result.soldQty}` : 'nothing to sell')
        toast.success(`${symbol} · Flattened — ${parts.join(', ')}`)
      } else {
        toast.error(`${symbol} · Flatten stopped — ${result.reason}`)
      }
      qc.invalidateQueries({ queryKey: ['portfolio'] })
      qc.invalidateQueries({ queryKey: ['app-orders'] })
      qc.invalidateQueries({ queryKey: ['ticker-fills'] })
    },
    onError: (err) => {
      setProgress(null)
      const msg =
        err instanceof ApiError
          ? `${err.message}: ${err.body || 'no detail'}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      toast.error(`${symbol} · Flatten failed — ${msg}`)
      qc.invalidateQueries({ queryKey: ['portfolio'] })
    },
  })

  return {
    flatten: () => mutation.mutate(),
    isPending: mutation.isPending,
    progress,
  }
}
